/*
 * vv-design-online.js — Design Online (/pages/design-online) card router.
 *
 * THE BUG THIS FIXES: on the Design Online page, clicking a product card opened the
 * gallery/designer with NO variant, so a multi-variant product (pull-up, media wall,
 * cube…) always loaded the FIRST variant's designs — the customer could never choose
 * their size/sides. The product-page flow already carries the chosen variant into the
 * gallery (snippets/vivid-buy-cards.liquid → __vvGalleryUrl); this brings the SAME
 * canonical-variant contract to the Design Online page.
 *
 * HOW: each card is routed by its product SHAPE (window.VVDesignConfig):
 *   DIRECT       → one orderable geometry: resolve it, launch (must be available).
 *   MODAL        → choose the design-affecting options (size / sides / …) in a modal
 *                  (bottom sheet on mobile) → resolve the canonical variant → launch.
 *   PRODUCT_PAGE → ITORIS custom-size product: hand off to /products/<h>?designOnline=1.
 *
 * The canonical variant comes from the ONE shared resolver (window.VVDesignResolver):
 * match the chosen design options, keep every other option at its product-page default.
 * No first-variant fallback, no price/D# heuristic. An unresolved or unavailable config
 * NEVER launches (CONFIG_REQUIRED fail-safe). Live variant data is fetched lazily from
 * /products/<handle>.js on first interaction and cached.
 *
 * Isolated: injects its own namespaced (.vvdoc-*) modal + styles; it only intercepts
 * primary clicks on cards whose handle is classified, and falls back to the card's
 * existing href if anything is missing — so if this file fails to load the page behaves
 * exactly as before. Modified-click (new tab) is left to the browser.
 */
(function () {
  "use strict";
  var TOOL = "https://create.vividads.com.au";
  // Single navigation seam. Defaults to a real page navigation; a test harness can set
  // window.__VVDO_NAV to capture the destination (and descriptor) without navigating.
  function nav(url, meta) { if (typeof window.__VVDO_NAV === "function") { window.__VVDO_NAV(url, meta || {}); return; } location.href = url; }

  function ready(fn) {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", fn, { once: true });
    else fn();
  }

  // Wait for the resolver + config globals (their <script>s load just before this one).
  function whenReady(fn, tries) {
    tries = tries == null ? 50 : tries;
    if (window.VVDesignResolver && window.VVDesignConfig && window.VVDesignEntry) return fn();
    if (tries <= 0) return; // leave native hrefs working
    setTimeout(function () { whenReady(fn, tries - 1); }, 60);
  }

  ready(function () { whenReady(init); });

  function init() {
    var root = document.getElementById("design-online");
    if (!root) return;
    var CFG = window.VVDesignConfig.CONFIG || {};
    var faceFor = window.VVDesignConfig.faceFor;
    var R = window.VVDesignResolver;
    // Q2 (2026-10-05): every destination URL comes from the ONE contract builder
    // (vv-design-entry.js — byte-identical to the create app's /lib/design-entry.js,
    // which gallery.html / designer.html use for their own links and Back).
    var E = window.VVDesignEntry;
    var SHOP = location.origin;   // same-origin product-page URLs, as before

    injectModal();

    [].slice.call(root.querySelectorAll(".prod")).forEach(function (card) {
      var handle = card.getAttribute("data-handle");
      if (!handle || !CFG[handle]) return; // unknown product → native href stays
      card.addEventListener("click", function (ev) {
        // honour new-tab / middle-click / modified clicks and let the browser do its thing
        if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
        ev.preventDefault();
        route(handle, card);
      });
    });

    // ---- product JSON cache (AJAX API), adapted to the resolver's shape ----
    var cache = {};
    function getProduct(handle, cb, attempt) {
      if (cache[handle]) return cb(cache[handle]);
      var url = "/products/" + encodeURIComponent(handle) + ".js";
      attempt = attempt || 1;
      fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
        .then(function (r) { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
        .then(function (pj) { var p = adaptAjax(pj); cache[handle] = p; cb(p); })
        .catch(function () {
          // Q2 (5 Oct 2026): one retry before the CONFIG_FALLBACK hand-off — a transient
          // network blip must not cost the customer the in-page size picker.
          if (attempt < 2) { setTimeout(function () { getProduct(handle, cb, attempt + 1); }, 400); return; }
          cb(null);
        });
    }

    // Q8 (5 Oct 2026): the card says what is happening while live sizes load (200–900 ms).
    function busy(card, on) {
      try {
        var go = card.querySelector(".go"); if (!go) return;
        if (on) { if (!card.hasAttribute("aria-busy")) card.__vvGo = go.textContent; go.textContent = "Loading sizes…"; card.setAttribute("aria-busy", "true"); }
        else { if (card.hasAttribute("aria-busy") && card.__vvGo != null) go.textContent = card.__vvGo; card.removeAttribute("aria-busy"); }
      } catch (e) {}
    }
    function route(handle, card) {
      var cfg = CFG[handle];
      var pid = card.getAttribute("data-pid") || "";
      var mode = currentMode();
      if (cfg.flow === "PRODUCT_PAGE") { nav(E.productPageUrl({ storefront: SHOP, handle: handle }), { flow: "PRODUCT_PAGE", handle: handle }); return; }
      busy(card, true);
      getProduct(handle, function (p) {
        busy(card, false);
        if (!p) {
          // 5 Oct 2026 (B9): the live options could not be loaded, so the size cannot be
          // chosen HERE. Go to the product page's configuration step (?needsize=1 — the
          // same place the gallery's own CONFIG_REQUIRED guard lands), never to the
          // gallery with no variant (that bounced through a second redirect) and never
          // to a default. DIRECT products have one geometry: the product page resolves it.
          toast("Loading this product’s sizes took too long — choose your size on the product page.");
          nav(E.needSizeUrl({ storefront: SHOP, handle: handle }, true), { flow: "CONFIG_FALLBACK", handle: handle });
          return;
        }
        if (cfg.flow === "DIRECT") {
          var r = R.resolveCanonicalVariant(p, cfg.designFields || [], {});
          if (r.status || r.available === false) { toast("Sorry, this product is currently unavailable."); return; }
          launch(handle, pid, mode, r, {});
          return;
        }
        openModal(handle, pid, p, mode, card);
      });
    }

    // ---- build the destination URL for the resolved variant + chosen way ----
    function launch(handle, pid, mode, r, chosen) {
      var fm = faceFor(handle, chosen) || { physicalFaceCount: 1, artworkSurfaceCount: 1, artworkMode: "SINGLE" };
      // Geometry is NOT carried in the URL: the server resolves the canvas from product +
      // variantTitle (the same rule the product page's "Explore design ideas" link relies on).
      var ctx = { tool: TOOL, storefront: SHOP, productId: pid, handle: handle, variantId: r.variantId, variantTitle: r.variantTitle };
      var url;
      if (mode === "ai") {
        url = E.designerUrl(ctx);
        // 24 Oct 11:47 (owner): "Design with AI" on a card opens the AI design chat with this product (and the size just
        // chosen) preselected. window.__vvChat (sections/vivid-design-online-start.liquid) health-checks the chat first;
        // if it is down, the existing designer opens exactly as before.
        if (window.__vvChat && typeof window.__vvChat.go === "function") {
          var designer = url, meta = { flow: CFG[handle].flow, handle: handle, mode: mode, variantId: r.variantId, variantTitle: r.variantTitle, widthMm: r.widthMm, heightMm: r.heightMm, physicalFaceCount: fm.physicalFaceCount, artworkMode: fm.artworkMode };
          window.__vvChat.go({ productId: pid, handle: handle, variantId: r.variantId, variantTitle: r.variantTitle }, function () { nav(designer, meta); }, "design_online_card");
          return;
        }
      } else if (mode === "blank") {
        // product page selects the variant from ?variant, then ?vvcc=blank opens the blank canvas at that size
        url = E.studioUrl(ctx, "blank");
      } else {
        // 'templates' and default (All) → the VividCanvas gallery (all four ways in one place)
        url = E.galleryUrl(ctx);
      }
      nav(url, { flow: CFG[handle].flow, handle: handle, mode: mode, variantId: r.variantId, variantTitle: r.variantTitle, widthMm: r.widthMm, heightMm: r.heightMm, physicalFaceCount: fm.physicalFaceCount, artworkMode: fm.artworkMode });
    }

    // ---- the active creation way (chip reflects it; '' = All/default) ----
    function currentMode() {
      var c = root.querySelector(".chip[aria-pressed=\"true\"]");
      return c ? (c.getAttribute("data-mode") || "") : "";
    }

    /* ============================ modal ============================ */
    var ms = null; // modal state
    function openModal(handle, pid, p, mode, card) {
      var cfg = CFG[handle];
      ms = { handle: handle, pid: pid, p: p, mode: mode, chosen: {}, custom: false };
      var title = (card.querySelector(".name") ? card.querySelector(".name").textContent : p.title) || "your product";
      byId("vvdoc-title").textContent = "Choose your size";
      byId("vvdoc-sub").textContent = title.trim() + " — pick the options that change your artwork. We’ll open the designer at the right size.";
      var fields = cfg.designFields || [];
      var html = "";
      fields.forEach(function (field) {
        var isSize = /size|shape/i.test(field);
        var isSides = /sides/i.test(field);
        var values = R.designFieldValues(p, field);
        if (!values.length) return;
        html += '<div class="vvdoc-field"><p class="vvdoc-label">' + esc(fieldLabel(field, isSides)) + '</p><div class="vvdoc-chips" data-field="' + escAttr(field) + '">';
        html += values.map(function (v) {
          if (cfg.customSizeValue && v === cfg.customSizeValue) {
            return '<button type="button" class="vvdoc-chip vvdoc-custom" data-v="' + escAttr(encodeURIComponent(v)) + '" data-custom="1">Custom size <span class="vvdoc-arrow">→</span></button>';
          }
          var avail = valueAvailable(p, field, v, {});
          var price = isSize ? priceFor(p, field, v, {}) : (isSides ? sidesExtra(v) : "");
          return '<button type="button" class="vvdoc-chip' + (avail ? "" : " vvdoc-dis") + '" data-v="' + escAttr(encodeURIComponent(v)) + '"' + (avail ? "" : " disabled") + '>' +
            esc(prettyValue(v, isSides)) + (price ? '<span class="vvdoc-price">' + esc(price) + '</span>' : "") +
            (avail ? "" : '<span class="vvdoc-na">Currently unavailable</span>') + '</button>';
        }).join("");
        html += '</div></div>';
      });
      html += '<p class="vvdoc-note" id="vvdoc-faces" style="display:none"></p>';
      byId("vvdoc-body").innerHTML = html;
      var cta = byId("vvdoc-cta");
      cta.disabled = true; cta.textContent = ctaLabel(mode);
      [].slice.call(byId("vvdoc-body").querySelectorAll(".vvdoc-chip:not(.vvdoc-dis)")).forEach(function (c) {
        c.addEventListener("click", function () { pickChip(c); });
      });
      ms.opener = card;
      showModal(true);
    }
    // Q8: the button names the destination the customer chose above
    function ctaLabel(mode) { return mode === "ai" ? "Continue to Design with AI" : mode === "blank" ? "Open a blank canvas" : "Continue to designs"; }

    function pickChip(c) {
      var group = c.parentElement, field = group.getAttribute("data-field");
      [].slice.call(group.querySelectorAll(".vvdoc-chip")).forEach(function (x) { x.classList.remove("vvdoc-sel"); });
      c.classList.add("vvdoc-sel");
      var cfg = CFG[ms.handle];
      if (c.getAttribute("data-custom")) {
        ms.custom = true; ms.chosen[field] = decodeURIComponent(c.getAttribute("data-v"));
        var ctaC = byId("vvdoc-cta"); ctaC.disabled = false; ctaC.textContent = "Enter a custom size →";
        var fcs = byId("vvdoc-faces"); fcs.style.display = "none";
        return;
      }
      ms.custom = false;
      ms.chosen[field] = decodeURIComponent(c.getAttribute("data-v"));

      // face note when a double-sided option is picked
      var sidesField = (cfg.designFields || []).find(function (f) { return /sides/i.test(f); });
      var faces = byId("vvdoc-faces");
      if (sidesField && ms.chosen[sidesField]) {
        var fm = faceFor(ms.handle, ms.chosen) || {};
        faces.style.display = "block";
        faces.innerHTML = (fm.physicalFaceCount > 1)
          ? "Double-sided → <b>2 printed faces</b>. We’ll use the same artwork on both by default; you can design the back differently in the editor."
          : "Single-sided → one printed face.";
      } else if (sidesField) { faces.style.display = "none"; }

      // enable CTA only when every design field is chosen AND the variant resolves + is available
      var complete = (cfg.designFields || []).every(function (f) { return ms.chosen[f]; });
      var cta = byId("vvdoc-cta");
      if (!complete) { cta.disabled = true; cta.textContent = ctaLabel(ms.mode); return; }
      var r = R.resolveCanonicalVariant(ms.p, cfg.designFields, ms.chosen);
      if (r.status) { cta.disabled = true; cta.textContent = "Choose your options"; }
      else if (r.available === false) { cta.disabled = true; cta.textContent = "That option is unavailable"; }
      else { cta.disabled = false; cta.textContent = ctaLabel(ms.mode); }
    }

    byId("vvdoc-cta").addEventListener("click", function () {
      if (!ms) return;
      var cfg = CFG[ms.handle];
      if (ms.custom) { showModal(false); nav(E.productPageUrl({ storefront: SHOP, handle: ms.handle }), { flow: "HYBRID_CUSTOM", handle: ms.handle }); return; }
      var r = R.resolveCanonicalVariant(ms.p, cfg.designFields, ms.chosen);
      if (r.status || r.available === false) return; // fail-safe: never launch unresolved/unavailable
      showModal(false);
      launch(ms.handle, ms.pid, ms.mode, r, ms.chosen);
    });

    // ---- availability / price helpers (non-D# base variants only) ----
    function variantOptionValue(v, name) {
      var so = (v.selectedOptions || []).find(function (o) { return o.name.trim() === String(name).trim(); });
      return so ? so.value : undefined;
    }
    function isBaseDisplay(v) { var d = variantOptionValue(v, "Display"); return !d || d.indexOf("D#") === -1; }
    function valueAvailable(p, field, value, chosen) {
      return (p.variants || []).some(function (vr) {
        if (variantOptionValue(vr, field) !== value) return false;
        if (vr.availableForSale === false) return false;
        if (!isBaseDisplay(vr)) return false;
        return Object.keys(chosen).every(function (k) { return k === field || variantOptionValue(vr, k) === chosen[k]; });
      });
    }
    function priceFor(p, field, value, chosen) {
      var best = null;
      (p.variants || []).forEach(function (vr) {
        if (variantOptionValue(vr, field) !== value || !isBaseDisplay(vr)) return;
        if (!Object.keys(chosen).every(function (k) { return k === field || variantOptionValue(vr, k) === chosen[k]; })) return;
        var pr = parseFloat(vr.price); if (isNaN(pr)) return;
        if (best == null || pr < best) best = pr;
      });
      return best == null ? "" : "From $" + Math.round(best);
    }
    function sidesExtra(v) { var m = /\+\s*AU\.?\$?(\d+)/i.exec(v); return m ? "+$" + m[1] : ""; }

    /* ---- modal plumbing ---- */
    function showModal(on) {
      var m = byId("vvdoc-backdrop");
      m.classList.toggle("vvdoc-show", !!on);
      document.documentElement.style.overflow = on ? "hidden" : "";
      // Q10 (5 Oct 2026): keyboard / screen-reader users land inside the dialog and return
      // to the card they opened it from. Tab is kept inside while it is open (see keydown).
      try {
        if (on) { var first = m.querySelector(".vvdoc-chip:not(.vvdoc-dis)") || byId("vvdoc-x"); if (first) first.focus(); }
        else if (ms && ms.opener && typeof ms.opener.focus === "function") ms.opener.focus();
      } catch (e) {}
    }
    function trapTab(e) {
      var m = byId("vvdoc-backdrop"); if (!m || !m.classList.contains("vvdoc-show") || e.key !== "Tab") return;
      var f = [].slice.call(m.querySelectorAll("button:not([disabled]),[href],input,select,textarea,[tabindex]:not([tabindex='-1'])")).filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    function injectModal() {
      if (byId("vvdoc-backdrop")) return;
      var style = document.createElement("style");
      style.textContent = VVDOC_CSS;
      document.head.appendChild(style);
      var wrap = document.createElement("div");
      wrap.innerHTML =
        '<div class="vvdoc-backdrop" id="vvdoc-backdrop" role="dialog" aria-modal="true" aria-labelledby="vvdoc-title">' +
          '<div class="vvdoc-modal">' +
            '<div class="vvdoc-head"><div><p class="vvdoc-title" id="vvdoc-title"></p><p class="vvdoc-sub" id="vvdoc-sub"></p></div>' +
            '<button class="vvdoc-close" id="vvdoc-x" type="button" aria-label="Close">&times;</button></div>' +
            '<div class="vvdoc-bd" id="vvdoc-body"></div>' +
            '<div class="vvdoc-foot"><button class="vvdoc-cta" id="vvdoc-cta" type="button" disabled>Continue to designs</button></div>' +
          '</div>' +
        '</div>';
      document.body.appendChild(wrap.firstChild);
      byId("vvdoc-x").addEventListener("click", function () { showModal(false); });
      byId("vvdoc-backdrop").addEventListener("click", function (e) { if (e.target === byId("vvdoc-backdrop")) showModal(false); });
      document.addEventListener("keydown", function (e) { if (e.key === "Escape" && byId("vvdoc-backdrop").classList.contains("vvdoc-show")) showModal(false); trapTab(e); });
    }

    function toast(msg) {
      var t = document.createElement("div");
      t.className = "vvdoc-toast"; t.textContent = msg;
      document.body.appendChild(t);
      requestAnimationFrame(function () { t.classList.add("vvdoc-show"); });
      setTimeout(function () { t.classList.remove("vvdoc-show"); setTimeout(function () { try { t.remove(); } catch (e) {} }, 300); }, 2600);
    }

    // ---- /products/<h>.js → resolver shape (preserves listed option order = product-page default) ----
    function adaptAjax(pj) {
      var names = (pj.options || []).map(function (o) { return typeof o === "string" ? o : o.name; });
      function optVal(v, i) { return v["option" + (i + 1)] != null ? v["option" + (i + 1)] : (v.options && v.options[i]); }
      var valsByName = {};
      names.forEach(function (n) { valsByName[n] = []; });
      (pj.variants || []).forEach(function (v) {
        names.forEach(function (n, i) { var val = optVal(v, i); if (val != null && valsByName[n].indexOf(val) === -1) valsByName[n].push(val); });
      });
      (pj.options || []).forEach(function (o) { if (o && o.name && o.values) valsByName[o.name] = o.values.slice(); });
      var options = names.map(function (n) { return { name: n, values: valsByName[n] || [] }; });
      var variants = (pj.variants || []).map(function (v) {
        return {
          id: String(v.id), title: v.title,
          availableForSale: v.available !== false,
          price: (v.price != null ? (v.price / 100).toFixed(2) : undefined),
          selectedOptions: names.map(function (n, i) { return { name: n, value: optVal(v, i) }; })
        };
      });
      return { handle: pj.handle, title: pj.title, id: String(pj.id), options: options, variants: variants };
    }

    // ---- small utils ----
    function byId(id) { return document.getElementById(id); }
    function esc(s) { return String(s == null ? "" : s).replace(/[&<>]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]; }); }
    function escAttr(s) { return String(s == null ? "" : s).replace(/"/g, "&quot;"); }
    function cleanSize(v) {
      return String(v).replace(/\(D#[^)]*\)|\(T#[^)]*\)/g, "").replace(/-?\s*SALE!?/ig, "").replace(/\s+/g, " ").trim().replace(/-\s*$/, "").trim();
    }
    function prettyValue(v, isSides) {
      if (isSides) { if (/double/i.test(v)) return "Double-sided"; if (/single/i.test(v)) return "Single-sided"; }
      return cleanSize(v);
    }
    function fieldLabel(field, isSides) {
      if (isSides) return "Printing";
      if (/size|shape/i.test(field)) return "Size";
      return field;
    }
  }

  var VVDOC_CSS =
    ".vvdoc-backdrop{position:fixed;inset:0;background:rgba(17,17,20,.46);display:none;align-items:center;justify-content:center;z-index:2147483100;padding:20px;font-family:Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif}" +
    ".vvdoc-backdrop.vvdoc-show{display:flex}" +
    ".vvdoc-modal{background:#fff;border-radius:16px;width:100%;max-width:460px;box-shadow:0 24px 60px rgba(0,0,0,.28);overflow:hidden;animation:vvdocIn .2s ease;color:#191919}" +
    "@keyframes vvdocIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}" +
    ".vvdoc-head{display:flex;align-items:flex-start;justify-content:space-between;padding:18px 18px 4px;gap:10px}" +
    ".vvdoc-title{font-size:18px;font-weight:800;letter-spacing:-.01em;margin:0}" +
    ".vvdoc-sub{font-size:12.5px;color:#666;margin:3px 0 0;line-height:1.35}" +
    ".vvdoc-close{border:none;background:none;font-size:24px;line-height:1;color:#9a9aa4;cursor:pointer;padding:0 2px}" +
    ".vvdoc-bd{padding:8px 18px 4px;max-height:60vh;overflow:auto}" +
    ".vvdoc-field{margin:12px 0 2px}" +
    ".vvdoc-label{font-size:12px;font-weight:700;color:#666;text-transform:uppercase;letter-spacing:.03em;margin:0 0 8px}" +
    ".vvdoc-chips{display:flex;flex-wrap:wrap;gap:8px}" +
    ".vvdoc-chip{border:1.5px solid #ececec;background:#fff;border-radius:11px;padding:10px 13px;font-size:14px;font-weight:600;cursor:pointer;transition:all .12s;line-height:1.15;color:#191919;position:relative;font-family:inherit}" +
    ".vvdoc-chip:hover{border-color:#020202}" +
    ".vvdoc-chip.vvdoc-sel{border-color:#020202;background:#020202;color:#fff}" +
    ".vvdoc-chip .vvdoc-price{display:block;font-size:11.5px;font-weight:600;opacity:.78;margin-top:2px}" +
    ".vvdoc-chip.vvdoc-dis{opacity:.5;cursor:not-allowed;background:#f6f6f7;border-color:#ececec;color:#666}" +
    ".vvdoc-chip.vvdoc-dis:hover{border-color:#ececec}" +
    ".vvdoc-chip .vvdoc-na{display:block;font-size:10.5px;font-weight:700;color:#b81b25;margin-top:2px;text-transform:uppercase;letter-spacing:.2px}" +
    ".vvdoc-chip.vvdoc-custom{border-style:dashed}.vvdoc-chip .vvdoc-arrow{opacity:.6;margin-left:3px}" +
    ".vvdoc-note{font-size:12px;color:#666;margin:10px 0 2px;line-height:1.4}" +
    ".vvdoc-foot{padding:14px 18px 18px}" +
    ".vvdoc-cta{width:100%;border:none;border-radius:12px;padding:14px;font-size:15px;font-weight:700;background:#D6202B;color:#fff;cursor:pointer;transition:opacity .15s;font-family:inherit}" +
    ".vvdoc-cta:hover{background:#b81b25}.vvdoc-cta:disabled{opacity:.4;cursor:not-allowed;background:#D6202B}" +
    ".vvdoc-toast{position:fixed;left:50%;bottom:26px;transform:translateX(-50%) translateY(12px);background:#1c1c1e;color:#fff;font-family:Inter,-apple-system,sans-serif;font-size:14px;font-weight:600;padding:12px 18px;border-radius:10px;box-shadow:0 10px 30px rgba(0,0,0,.25);z-index:2147483200;opacity:0;transition:opacity .25s,transform .25s;pointer-events:none}" +
    ".vvdoc-toast.vvdoc-show{opacity:1;transform:translateX(-50%) translateY(0)}" +
    "@media(max-width:560px){.vvdoc-backdrop{align-items:flex-end;padding:0}.vvdoc-modal{max-width:none;border-radius:18px 18px 0 0;animation:vvdocUp .22s ease}.vvdoc-bd{max-height:64vh}}" +
    "@keyframes vvdocUp{from{transform:translateY(100%)}to{transform:none}}";
})();
